import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePollingFallback } from '@/hooks/usePollingFallback';
import { THREAD_VIEWED_PREFIX } from '@/types/omniChannel';

// Hook for getting global unread message count
export function useUnreadMessagesCount() {
  const refetchInterval = usePollingFallback(5 * 60_000);

  return useQuery({
    queryKey: ['unread-messages-count'],
    queryFn: async (): Promise<number> => {
      // Fetch recent inbound SMS messages. The 30-day cap prevents a CTM
      // historical backfill from spiking the unread badge with old messages.
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: messages, error } = await supabase
        .from('sms_messages')
        .select('id, sent_at, lead_id, customer_id, tracking_number')
        .eq('direction', 'inbound')
        .gte('sent_at', thirtyDaysAgo)
        .order('sent_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      let unreadCount = 0;

      // Group by thread and check localStorage for read status
      const threadLatestViewed = new Map<string, Date>();

      for (const msg of messages || []) {
        const threadKey = msg.lead_id
          ? `lead:${msg.lead_id}:${msg.tracking_number || 'default'}`
          : msg.customer_id
            ? `customer:${msg.customer_id}:${msg.tracking_number || 'default'}`
            : null;

        if (!threadKey) continue;

        // Get cached last viewed time or fetch from localStorage
        if (!threadLatestViewed.has(threadKey)) {
          const lastViewed = localStorage.getItem(`${THREAD_VIEWED_PREFIX}${threadKey}`);
          threadLatestViewed.set(
            threadKey,
            lastViewed ? new Date(lastViewed) : new Date(0)
          );
        }

        const lastViewed = threadLatestViewed.get(threadKey)!;
        if (new Date(msg.sent_at) > lastViewed) {
          unreadCount++;
        }
      }

      return unreadCount;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval,
  });
}
